if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.form = this.querySelector('.tt-cart-discount__form');
        this.input = this.querySelector('.field__input');
        this.applyButton = this.querySelector('[data-discount-apply]');
        this.applyLabel = this.querySelector('[data-discount-apply-label]');
        this.status = this.querySelector('[data-discount-status]');
        this.syncField = this.querySelector('.tt-cart-discount__sync');
        this.applyLabelText = this.applyLabel ? this.applyLabel.textContent : '';

        this.form?.addEventListener('submit', this.onSubmit.bind(this));
        this.input?.addEventListener('input', this.onInput.bind(this));
        this.toggle = this.querySelector('[data-discount-toggle]');
        this.panel = this.querySelector('.tt-cart-discount__panel');
        this.toggle?.addEventListener('click', this.onToggle.bind(this));
        this.querySelectorAll('[data-discount-remove]').forEach((button) => {
          button.addEventListener('click', this.onRemove.bind(this));
        });
        this.bindAcceleratedCheckoutGuard();
      }

      onToggle() {
        const open = this.toggle?.getAttribute('aria-expanded') === 'true';
        this.setOpen(!open);
      }

      setOpen(open) {
        if (!this.toggle || !this.panel) return;
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.panel.hidden = !open;
        if (open) this.input?.focus();
      }

      onInput() {
        const value = (this.input?.value || '').trim();
        if (this.syncField) {
          this.syncField.value = value;
          if (value) {
            this.syncField.setAttribute('name', 'discount');
          } else {
            this.syncField.removeAttribute('name');
          }
        }
        this.clearStatus();
      }

      async onSubmit(event) {
        event.preventDefault();
        const code = (this.input?.value || '').trim();
        if (!code) {
          this.showError(this.dataset.errorEmpty);
          this.input?.focus();
          return;
        }
        await this.applyCode(code);
      }

      async onRemove(event) {
        event.preventDefault();
        this.setLoading(true);
        try {
          await this.updateCartDiscount('');
          this.reloadOrRefresh();
        } catch (error) {
          this.showError(this.dataset.errorGeneric);
          this.setLoading(false);
        }
      }

      bindAcceleratedCheckoutGuard() {
        const root = this.closest('.cart__blocks') || this.closest('.drawer__footer');
        const buttons = root?.querySelector('.additional-checkout-buttons');
        if (!buttons || buttons.dataset.ttDiscountGuard === 'true') return;
        buttons.dataset.ttDiscountGuard = 'true';
        buttons.addEventListener('click', this.onAcceleratedCheckoutClick.bind(this), true);
      }

      async onAcceleratedCheckoutClick(event) {
        const code = (this.input?.value || '').trim();
        if (!code) return;
        event.preventDefault();
        event.stopPropagation();
        await this.applyCode(code);
      }

      async applyCode(code) {
        this.setLoading(true);
        try {
          const result = await this.submitCode(code);
          if (result === 'applied') {
            if (this.syncField) this.syncField.value = code;
            this.reloadOrRefresh();
            return;
          }
          if (result === 'not_applicable') {
            this.showError(this.dataset.errorNotApplicable);
          } else {
            this.showError(this.dataset.errorInvalid);
          }
          this.setLoading(false);
        } catch (error) {
          this.showError(this.dataset.errorGeneric);
          this.setLoading(false);
        }
      }

      async submitCode(code) {
        try {
          const cart = await this.updateCartDiscount(code);
          const match = this.matchCode(cart, code);
          if (match) return match;
        } catch (error) {
          // Fall through to the /discount URL, which works on all stores.
        }

        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        await fetch(`${root}discount/${encodeURIComponent(code)}`, {
          credentials: 'same-origin',
          redirect: 'follow',
        });
        const cart = await this.fetchCart();
        return this.matchCode(cart, code) || 'invalid';
      }

      async updateCartDiscount(code) {
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              };
        const response = await fetch(`${window.routes.cart_update_url}`, {
          ...config,
          body: JSON.stringify({ discount: code }),
        });
        const cart = await response.json();
        if (!response.ok) {
          throw new Error(cart?.description || cart?.message || cart?.errors || 'Discount update failed');
        }
        return cart;
      }

      async fetchCart() {
        const response = await fetch(`${window.routes.cart_url}.js`, {
          credentials: 'same-origin',
        });
        return response.json();
      }

      matchCode(cart, code) {
        if (!cart) return null;
        const target = code.toLowerCase();
        const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        const found = codes.find((entry) => (entry.code || '').toLowerCase() === target);
        if (found) {
          return found.applicable === false ? 'not_applicable' : 'applied';
        }

        const applications = Array.isArray(cart.cart_level_discount_applications)
          ? cart.cart_level_discount_applications
          : [];
        const titleMatch = applications.some((entry) => (entry.title || '').toLowerCase() === target);
        if (titleMatch) return 'applied';

        const items = Array.isArray(cart.items) ? cart.items : [];
        const lineMatch = items.some((item) =>
          (item.discounts || []).some((entry) => (entry.title || '').toLowerCase() === target)
        );
        return lineMatch ? 'applied' : null;
      }

      reloadOrRefresh() {
        if (document.getElementById('main-cart-footer')) {
          window.location.reload();
          return;
        }
        this.refreshDrawer();
      }

      refreshDrawer() {
        return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectors = ['cart-drawer-items', '.cart-drawer__footer', 'cart-discount'];
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }
          })
          .catch(() => {
            window.location.reload();
          });
      }

      setLoading(isLoading) {
        if (this.applyButton) this.applyButton.disabled = isLoading;
        if (this.input) this.input.disabled = isLoading;
        this.querySelectorAll('[data-discount-remove]').forEach((button) => {
          button.disabled = isLoading;
        });
        if (this.applyLabel) {
          this.applyLabel.textContent = isLoading
            ? this.dataset.applyLoading || this.applyLabelText
            : this.applyLabelText;
        }
        this.classList.toggle('is-loading', isLoading);
      }

      showError(message) {
        this.setOpen(true);
        if (!this.status) return;
        this.status.textContent = message || '';
        this.status.classList.add('tt-cart-discount__status--error');
        this.input?.classList.add('field__input--error');
      }

      clearStatus() {
        if (!this.status) return;
        this.status.textContent = '';
        this.status.classList.remove('tt-cart-discount__status--error');
        this.input?.classList.remove('field__input--error');
      }
    }
  );
}
